import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useFavoriteArtisanIds = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["favorite-artisan-ids", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("favorite_artisans")
        .select("artisan_user_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return new Set(data.map((f) => f.artisan_user_id));
    },
  });
};

export const useFavoriteArtisans = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["favorite-artisans", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("favorite_artisans")
        .select("id, artisan_user_id, created_at")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Fetch artisan profiles + display names
      const artisanIds = data.map((f) => f.artisan_user_id);
      if (artisanIds.length === 0) return [];

      const { data: artisans } = await supabase
        .from("artisan_profiles")
        .select("user_id, specialite, wilaya, commune, type, logo_url, is_reactive_badge, response_rate, availability, verification_status")
        .in("user_id", artisanIds);

      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", artisanIds);

      const artisanMap = new Map((artisans ?? []).map((a) => [a.user_id, a]));
      const profileMap = new Map((profiles ?? []).map((p) => [p.user_id, p]));

      return data.map((fav) => ({
        ...fav,
        artisan: artisanMap.get(fav.artisan_user_id),
        profile: profileMap.get(fav.artisan_user_id),
      }));
    },
  });
};

export const useToggleFavoriteArtisan = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (artisanUserId: string) => {
      if (!user) throw new Error("Non connecté");

      const { data: existing } = await supabase
        .from("favorite_artisans")
        .select("id")
        .eq("user_id", user.id)
        .eq("artisan_user_id", artisanUserId)
        .maybeSingle();

      if (existing) {
        await supabase.from("favorite_artisans").delete().eq("id", existing.id);
      } else {
        await supabase.from("favorite_artisans").insert({ user_id: user.id, artisan_user_id: artisanUserId });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorite-artisan-ids"] });
      qc.invalidateQueries({ queryKey: ["favorite-artisans"] });
    },
  });
};
